import type {
  CustomerValueCriterionDefinition,
  CustomerValueCriterionManagementErrorCode,
  CustomerValueCriterionScoringBand,
  CustomerValueCriterionType,
  CustomerValueCriterionUnitType,
  CustomerValueCriterionValueType,
  CustomerValueRecordStatus,
  CustomerValueScoringConfigurationStatus,
  CustomerValueScoringDirection,
  CustomerValueSourceStrategy
} from "./criterionScoring";

export type CustomerValueCriterionUiLanguage = "de" | "en";

export interface CustomerValueCriterionDeleteContext {
  criterion: Pick<CustomerValueCriterionDefinition, "scoringConfigurationStatus">;
  hasHistoricalUsage?: boolean;
  hasConfirmedScoringConfiguration?: boolean;
  hasActiveCrmMapping?: boolean;
}

export interface CustomerValueCriterionDeleteDecision {
  allowed: boolean;
  code?: CustomerValueCriterionManagementErrorCode;
}

type LabelDictionary<T extends string> = Record<T, Record<CustomerValueCriterionUiLanguage, string>>;

export type CustomerValueHelpKey =
  | "criterion"
  | "category"
  | "weight"
  | "descriptionSuggestion"
  | "evaluationType"
  | "valueType"
  | "unitType"
  | "scoringDirection"
  | "sourceStrategy"
  | "configurationStatus"
  | "requiredField"
  | "activeField"
  | "points"
  | "valueRange"
  | "rangeBoundaries"
  | "legacyReplacement"
  | "duplicateCriterion"
  | "deactivateCriterion"
  | "deleteCriterion"
  | "scoringPreview"
  | "segmentDefinition"
  | "modelType"
  | "crmTransparency";

export interface CustomerValueHelpContent {
  title: Record<CustomerValueCriterionUiLanguage, string>;
  explanation: Record<CustomerValueCriterionUiLanguage, string>;
  impact: Record<CustomerValueCriterionUiLanguage, string>;
  example: Record<CustomerValueCriterionUiLanguage, string>;
  warning?: Record<CustomerValueCriterionUiLanguage, string>;
}

export type CustomerValueCrmTransparencyField =
  | "criterionName"
  | "rawValue"
  | "points"
  | "weight"
  | "weightedContribution"
  | "modelVersion"
  | "evaluatedAt"
  | "missingValue"
  | "errorStatus";

export interface CustomerValueCrmTransparencyFieldDefinition {
  key: CustomerValueCrmTransparencyField;
  label: Record<CustomerValueCriterionUiLanguage, string>;
  purpose: Record<CustomerValueCriterionUiLanguage, string>;
  writebackObject: "company";
  writebackTiming: "future_version_only";
}

export const CRITERION_TYPE_LABELS: LabelDictionary<CustomerValueCriterionType> = {
  legacy: { de: "Bisherige Bewertungslogik", en: "Existing assessment logic" },
  quantitative: { de: "Zahlenwert", en: "Numeric value" },
  qualitative: { de: "Auswahl oder Einschätzung", en: "Selection or assessment" },
  boolean: { de: "Ja/Nein", en: "Yes/No" },
  calculated: { de: "Berechneter Wert", en: "Calculated value" }
};

export const VALUE_TYPE_LABELS: LabelDictionary<CustomerValueCriterionValueType> = {
  unknown: { de: "Noch nicht festgelegt", en: "Not selected yet" },
  currency: { de: "Währung", en: "Currency" },
  percentage: { de: "Prozent", en: "Percentage" },
  integer: { de: "Ganze Zahl", en: "Integer" },
  decimal: { de: "Dezimalzahl", en: "Decimal number" },
  days: { de: "Tage", en: "Days" },
  months: { de: "Monate", en: "Months" },
  years: { de: "Jahre", en: "Years" },
  count: { de: "Anzahl", en: "Count" },
  text_choice: { de: "Auswahlliste", en: "Selection list" },
  boolean: { de: "Ja/Nein", en: "Yes/No" }
};

export const UNIT_TYPE_LABELS: LabelDictionary<CustomerValueCriterionUnitType> = {
  EUR: { de: "Workspace-Währung", en: "Workspace currency" },
  percentage: { de: "Prozent", en: "Percentage" },
  count: { de: "Anzahl", en: "Count" },
  days: { de: "Tage", en: "Days" },
  months: { de: "Monate", en: "Months" },
  years: { de: "Jahre", en: "Years" },
  custom: { de: "frei definierte Einheit", en: "custom unit" },
  none: { de: "keine Einheit", en: "no unit" }
};

export const SCORING_DIRECTION_LABELS: LabelDictionary<CustomerValueScoringDirection> = {
  higher_is_better: { de: "Höherer Wert ist besser", en: "Higher value is better" },
  lower_is_better: { de: "Niedrigerer Wert ist besser", en: "Lower value is better" },
  manual_bands: { de: "Wertebereiche individuell festlegen", en: "Define value ranges manually" }
};

export const SOURCE_STRATEGY_LABELS: LabelDictionary<CustomerValueSourceStrategy> = {
  legacy_assessment: { de: "Bestehende Bewertungslogik", en: "Existing assessment logic" },
  manual: { de: "Manuell gepflegt", en: "Maintained manually" },
  crm_mapped: { de: "Aus CRM-Feld", en: "From CRM field" },
  customer_value_process: { de: "Aus Customer-Value-Prozess", en: "From Customer Value process" },
  deal_win_engine: { de: "Aus Deal Win Engine", en: "From Deal Win Engine" },
  calculated_formula: { de: "Berechnete Formel", en: "Calculated formula" }
};

export const SCORING_STATUS_LABELS: LabelDictionary<CustomerValueScoringConfigurationStatus> = {
  unconfigured: { de: "Noch nicht konfiguriert", en: "Not configured yet" },
  draft: { de: "Draft", en: "Draft" },
  confirmed: { de: "Bestätigt", en: "Confirmed" },
  superseded: { de: "Ersetzt", en: "Superseded" }
};

export const RECORD_STATUS_LABELS: LabelDictionary<CustomerValueRecordStatus> = {
  draft: { de: "Draft", en: "Draft" },
  confirmed: { de: "Bestätigt", en: "Confirmed" },
  superseded: { de: "Ersetzt", en: "Superseded" },
  archived: { de: "Archiviert", en: "Archived" }
};

export const CUSTOMER_VALUE_HELP_TEXTS: Record<CustomerValueHelpKey, CustomerValueHelpContent> = {
  criterion: {
    title: { de: "Atomares Kriterium", en: "Atomic criterion" },
    explanation: {
      de: "Ein Kriterium beschreibt genau eine eigenständig mess- oder bewertbare Dimension des Kundenwerts.",
      en: "A criterion describes one independently measurable or assessable customer value dimension."
    },
    impact: {
      de: "Umsatz, Marge, Aufwand oder strategische Passung werden getrennt geführt, wenn sie getrennt in den Score eingehen sollen.",
      en: "Revenue, margin, effort or strategic fit are kept separate when they should contribute separately to the score."
    },
    example: {
      de: "Statt „Wirtschaftlicher Beitrag“ können „Umsatzpotenzial“ und „Margenqualität“ eigene Kriterien sein.",
      en: "Instead of “commercial contribution”, “revenue potential” and “margin quality” can be separate criteria."
    }
  },
  category: {
    title: { de: "Kategorie", en: "Category" },
    explanation: {
      de: "Kategorien sind eine optionale Ordnungshilfe für Kriterien und gehören nicht zur Bewertungslogik.",
      en: "Categories are optional organization metadata for criteria and are not part of the scoring logic."
    },
    impact: {
      de: "Sie beeinflussen keine Punkte, keine Gewichtung und keinen Customer-Value-Score.",
      en: "They have no points and do not affect weighting or the customer value score."
    },
    example: {
      de: "Beispiel: Wirtschaftlichkeit, Beziehung, strategische Relevanz.",
      en: "Example: commercial value, relationship, strategic relevance."
    },
    warning: {
      de: "Eine Kategorie ist kein Bewertungskriterium und wird später nicht als eigener Score ins CRM geschrieben.",
      en: "A category is not an assessment criterion and will not be written to CRM as its own score."
    }
  },
  descriptionSuggestion: {
    title: { de: "Beschreibungsvorschlag", en: "Description suggestion" },
    explanation: {
      de: "HAUFFE OS kann aus Modelltyp, Kriterium und vorhandenen Workshop-Signalen eine fachliche Beschreibung vorschlagen.",
      en: "HAUFFE OS can suggest a business description from model type, criterion and available workshop signals."
    },
    impact: {
      de: "Der Vorschlag hilft bei einheitlicher Methodik, überschreibt bestehende Beschreibungen aber nie automatisch.",
      en: "The suggestion helps keep methodology consistent but never overwrites existing descriptions automatically."
    },
    example: {
      de: "Für Prospects wird Erwartungssprache genutzt, für Bestandskunden Sprache zum tatsächlichen Kundenwert.",
      en: "Prospects use expectation-based wording, existing customers use realized customer value wording."
    }
  },
  weight: {
    title: { de: "Gewichtung", en: "Weighting" },
    explanation: {
      de: "Die Gewichtung beschreibt, wie stark ein aktives Kriterium in den Gesamtscore eingeht.",
      en: "Weighting defines how strongly an active criterion contributes to the overall score."
    },
    impact: {
      de: "Nur aktive Kriterien zählen. Zusammen müssen sie exakt 100 Prozent ergeben.",
      en: "Only active criteria count. Together they must equal exactly 100 percent."
    },
    example: {
      de: "Zwei Ersatzkriterien können ein altes 20-%-Sammelkriterium zum Beispiel mit 10 % und 10 % übernehmen.",
      en: "Two replacement criteria can take over an old 20% combined criterion with 10% and 10%, for example."
    },
    warning: {
      de: "HAUFFE OS verteilt Gewichtungen nie automatisch um.",
      en: "HAUFFE OS never redistributes weights automatically."
    }
  },
  evaluationType: {
    title: { de: "Bewertungstyp", en: "Evaluation type" },
    explanation: {
      de: "Der Typ legt fest, ob ein Kriterium als Zahl, Auswahl, Ja/Nein-Wert oder nur als bisherige Logik geführt wird.",
      en: "The type defines whether a criterion is numeric, a selection, a yes/no value or existing logic only."
    },
    impact: {
      de: "Er steuert, welche Eingaben und Bewertungslogiken fachlich sinnvoll sind.",
      en: "It controls which inputs and scoring rules make sense."
    },
    example: {
      de: "Marge ist meist ein Zahlenwert, Referenzfähigkeit eher eine Auswahl.",
      en: "Margin is usually numeric, reference potential is often a selection."
    }
  },
  valueType: {
    title: { de: "Wertetyp", en: "Value type" },
    explanation: {
      de: "Der Wertetyp beschreibt die fachliche Form des Eingangswerts.",
      en: "The value type describes the business form of the input value."
    },
    impact: {
      de: "Er verbessert Formatierung, Prüfung und spätere CRM-Zuordnung.",
      en: "It improves formatting, validation and later CRM mapping."
    },
    example: {
      de: "Umsatzpotenzial als Währung, Rabattquote als Prozentwert.",
      en: "Revenue potential as currency, discount rate as percentage."
    }
  },
  unitType: {
    title: { de: "Einheit", en: "Unit" },
    explanation: {
      de: "Die Einheit macht Zahlen lesbar und verhindert Missverständnisse.",
      en: "The unit makes numeric values readable and avoids misunderstandings."
    },
    impact: {
      de: "Sie ändert nicht automatisch die Punkte, hilft aber bei Eingabe und CRM-Transparenz.",
      en: "It does not automatically change points, but helps with input and CRM transparency."
    },
    example: {
      de: "EUR, Prozent, Anzahl, Tage.",
      en: "EUR, percentage, count, days."
    }
  },
  scoringDirection: {
    title: { de: "Bewertungsrichtung", en: "Scoring direction" },
    explanation: {
      de: "Die Richtung beschreibt, ob höhere oder niedrigere Werte bessere Kundenwert-Signale sind.",
      en: "The direction describes whether higher or lower values are better customer value signals."
    },
    impact: {
      de: "Sie steuert Vorschläge für Wertebereiche und macht die Logik nachvollziehbar.",
      en: "It guides range suggestions and makes the logic transparent."
    },
    example: {
      de: "Höhere Marge ist besser, niedriger Betreuungsaufwand ist besser.",
      en: "Higher margin is better, lower service effort is better."
    }
  },
  sourceStrategy: {
    title: { de: "Datenquelle", en: "Data source" },
    explanation: {
      de: "Die Datenquelle zeigt, ob Werte manuell, aus CRM, aus dem Customer-Value-Prozess oder später berechnet kommen.",
      en: "The data source shows whether values are manual, from CRM, from the Customer Value process or calculated later."
    },
    impact: {
      de: "Das schafft Transparenz, ohne schon ein CRM-Mapping zu aktivieren.",
      en: "This creates transparency without activating CRM mapping yet."
    },
    example: {
      de: "Umsatzpotenzial kann manuell gepflegt oder später aus einem CRM-Feld gelesen werden.",
      en: "Revenue potential can be maintained manually or later read from a CRM field."
    }
  },
  configurationStatus: {
    title: { de: "Konfigurationsstatus", en: "Configuration status" },
    explanation: {
      de: "Der Status trennt unkonfigurierte, Draft- und bestätigte Bewertungslogik.",
      en: "The status separates unconfigured, draft and confirmed scoring logic."
    },
    impact: {
      de: "Bestätigte Logik bleibt geschützt; Änderungen werden als Draft vorbereitet.",
      en: "Confirmed logic stays protected; changes are prepared as drafts."
    },
    example: {
      de: "Ein neues Ersatzkriterium startet als Draft.",
      en: "A new replacement criterion starts as a draft."
    }
  },
  requiredField: {
    title: { de: "Pflichtfeld", en: "Required field" },
    explanation: {
      de: "Pflichtfelder markieren Werte, die für eine belastbare Bewertung vorliegen sollten.",
      en: "Required fields mark values that should be present for a reliable assessment."
    },
    impact: {
      de: "Fehlende Pflichtwerte können eine Bewertung später als unvollständig kennzeichnen.",
      en: "Missing required values can mark a later assessment as incomplete."
    },
    example: {
      de: "Marge oder Umsatzpotenzial können als Pflichtsignal definiert werden.",
      en: "Margin or revenue potential can be defined as required signals."
    }
  },
  activeField: {
    title: { de: "Aktiv", en: "Active" },
    explanation: {
      de: "Nur aktive Kriterien zählen in Gewichtung und Bewertung.",
      en: "Only active criteria count in weighting and assessment."
    },
    impact: {
      de: "Ein deaktiviertes Sammelkriterium bleibt nachvollziehbar, beeinflusst aber den Score nicht mehr.",
      en: "A deactivated combined criterion remains traceable but no longer affects the score."
    },
    example: {
      de: "Beim Ersetzen wird das alte Kriterium erst im Draft deaktiviert.",
      en: "During replacement, the old criterion is first deactivated in the draft."
    }
  },
  points: {
    title: { de: "Punkte", en: "Points" },
    explanation: {
      de: "Punkte übersetzen einen Eingangswert in eine Bewertung von 1 bis 10.",
      en: "Points translate an input value into a score from 1 to 10."
    },
    impact: {
      de: "Zusammen mit der Gewichtung entsteht daraus der Beitrag zum Customer-Value-Score.",
      en: "Together with weighting, this creates the contribution to the customer value score."
    },
    example: {
      de: "Sehr hoher Umsatzbeitrag kann 10 Punkte erhalten.",
      en: "Very high revenue contribution can receive 10 points."
    }
  },
  valueRange: {
    title: { de: "Wertebereich", en: "Value range" },
    explanation: {
      de: "Ein Wertebereich legt fest, welche Eingangswerte zu welchen Punkten führen.",
      en: "A value range defines which input values lead to which points."
    },
    impact: {
      de: "Die lesbare Vorschau zeigt die Logik so, wie sie später verstanden werden soll.",
      en: "The readable preview shows the logic as it should later be understood."
    },
    example: {
      de: "Von 50.000 bis 100.000 EUR ergibt 6 Punkte.",
      en: "From EUR 50,000 to EUR 100,000 gives 6 points."
    }
  },
  rangeBoundaries: {
    title: { de: "Grenzen", en: "Boundaries" },
    explanation: {
      de: "Erweiterte Grenzen steuern, ob ein Bereich offen oder inklusive ist.",
      en: "Advanced boundaries control whether a range is open or inclusive."
    },
    impact: {
      de: "Diese Details verhindern Lücken oder Überschneidungen in der Bewertung.",
      en: "These details prevent gaps or overlaps in scoring."
    },
    example: {
      de: "Der höchste Bereich kann nach oben offen sein.",
      en: "The highest range can be open-ended."
    },
    warning: {
      de: "Nur ändern, wenn die Fachlogik es wirklich braucht.",
      en: "Change only when the business logic really requires it."
    }
  },
  legacyReplacement: {
    title: { de: "Sammelkriterium ersetzen", en: "Replace combined criterion" },
    explanation: {
      de: "Der geführte Flow zerlegt ein altes Sammelkriterium in mehrere klare, einzeln bewertbare Kriterien.",
      en: "The guided flow splits an old combined criterion into several clear, individually assessable criteria."
    },
    impact: {
      de: "Dadurch wird die Bewertung nachvollziehbarer und später CRM-fähiger.",
      en: "This makes the assessment more transparent and later CRM-ready."
    },
    example: {
      de: "Aus Wirtschaftlicher Beitrag werden zum Beispiel Umsatzpotenzial und Marge.",
      en: "Commercial contribution can become revenue potential and margin, for example."
    },
    warning: {
      de: "Die Ersetzung bleibt ein Draft und wird nicht automatisch bestätigt.",
      en: "The replacement remains a draft and is not confirmed automatically."
    }
  },
  duplicateCriterion: {
    title: { de: "Duplizieren", en: "Duplicate" },
    explanation: {
      de: "Erstellt eine Draft-Kopie als Ausgangspunkt für ein neues Kriterium.",
      en: "Creates a draft copy as the starting point for a new criterion."
    },
    impact: {
      de: "Das Original bleibt unverändert.",
      en: "The original stays unchanged."
    },
    example: {
      de: "Nützlich für ähnliche Bewertungslogiken.",
      en: "Useful for similar scoring logic."
    }
  },
  deactivateCriterion: {
    title: { de: "Deaktivieren", en: "Deactivate" },
    explanation: {
      de: "Entfernt ein Kriterium aus der aktiven Gewichtung, ohne es zu löschen.",
      en: "Removes a criterion from active weighting without deleting it."
    },
    impact: {
      de: "Die Historie bleibt lesbar.",
      en: "History remains readable."
    },
    example: {
      de: "Sinnvoll für ersetzte Sammelkriterien.",
      en: "Useful for replaced combined criteria."
    }
  },
  deleteCriterion: {
    title: { de: "Draft löschen", en: "Delete draft" },
    explanation: {
      de: "Löscht nur zulässige Draft-Elemente ohne bestätigte Nutzung.",
      en: "Deletes only eligible draft items without confirmed usage."
    },
    impact: {
      de: "Bestätigte oder historisch verwendete Kriterien bleiben geschützt.",
      en: "Confirmed or historically used criteria stay protected."
    },
    example: {
      de: "Ein versehentlich angelegtes Draft-Kriterium kann entfernt werden.",
      en: "An accidentally created draft criterion can be removed."
    },
    warning: {
      de: "Kategorien mit Kriterien werden nicht gelöscht.",
      en: "Categories containing criteria are not deleted."
    }
  },
  scoringPreview: {
    title: { de: "Preview", en: "Preview" },
    explanation: {
      de: "Die Preview prüft Testwerte gegen die aktuelle Draft-Logik.",
      en: "The preview checks test values against the current draft logic."
    },
    impact: {
      de: "Sie speichert nichts und bestätigt keine neue Version.",
      en: "It saves nothing and confirms no new version."
    },
    example: {
      de: "Testen Sie, ob 20 % Marge die erwarteten Punkte erhält.",
      en: "Test whether 20% margin receives the expected points."
    }
  },
  segmentDefinition: {
    title: { de: "Segmentdefinition", en: "Segment definition" },
    explanation: {
      de: "Segmente ordnen den finalen Customer-Value-Score in verständliche Kundenwertstufen ein.",
      en: "Segments translate the final customer value score into understandable value tiers."
    },
    impact: {
      de: "Sie sind getrennt von einzelnen Kriterien und werden versioniert.",
      en: "They are separate from individual criteria and versioned."
    },
    example: {
      de: "Sehr starker Kundenwert, stabiler Potenzialkunde.",
      en: "Very strong customer value, stable potential customer."
    }
  },
  modelType: {
    title: { de: "Modelltyp", en: "Model type" },
    explanation: {
      de: "Der Modelltyp trennt Bestandskundenwert von New-Customer-Value-Logik.",
      en: "The model type separates existing-customer value from new-customer value logic."
    },
    impact: {
      de: "Er hilft, Kriterien fachlich passend einzuordnen.",
      en: "It helps classify criteria in the right business context."
    },
    example: {
      de: "Bestandskunden können Serviceaufwand und Retention stärker berücksichtigen.",
      en: "Existing customers can weigh service effort and retention more strongly."
    }
  },
  crmTransparency: {
    title: { de: "CRM-Transparenz", en: "CRM transparency" },
    explanation: {
      de: "Spätere CRM-Felder sollen zeigen, welcher Rohwert zu welchen Punkten geführt hat.",
      en: "Future CRM fields should show which raw value led to which points."
    },
    impact: {
      de: "Kategorien bleiben Organisation, Kriterien werden die nachweisbaren Score-Treiber.",
      en: "Categories stay organizational; criteria become the traceable score drivers."
    },
    example: {
      de: "Rohwert, Punkte, Gewichtung und gewichteter Beitrag je Kriterium.",
      en: "Raw value, points, weighting and weighted contribution per criterion."
    },
    warning: {
      de: "In diesem Schritt wird kein CRM-Mapping aktiviert.",
      en: "No CRM mapping is activated in this step."
    }
  }
} as const;

export const CRITERION_TOOLTIPS = {
  category: {
    de: CUSTOMER_VALUE_HELP_TEXTS.category.impact.de,
    en: CUSTOMER_VALUE_HELP_TEXTS.category.impact.en
  },
  weight: {
    de: CUSTOMER_VALUE_HELP_TEXTS.weight.impact.de,
    en: CUSTOMER_VALUE_HELP_TEXTS.weight.impact.en
  },
  scoringDirection: {
    de: `${CUSTOMER_VALUE_HELP_TEXTS.scoringDirection.impact.de} ${CUSTOMER_VALUE_HELP_TEXTS.scoringDirection.warning?.de ?? ""}`.trim(),
    en: `${CUSTOMER_VALUE_HELP_TEXTS.scoringDirection.impact.en} ${CUSTOMER_VALUE_HELP_TEXTS.scoringDirection.warning?.en ?? ""}`.trim()
  },
  legacyReplacement: {
    de: CUSTOMER_VALUE_HELP_TEXTS.legacyReplacement.warning?.de ?? CUSTOMER_VALUE_HELP_TEXTS.legacyReplacement.impact.de,
    en: CUSTOMER_VALUE_HELP_TEXTS.legacyReplacement.warning?.en ?? CUSTOMER_VALUE_HELP_TEXTS.legacyReplacement.impact.en
  }
} as const;

export const CUSTOMER_VALUE_CRM_TRANSPARENCY_FIELDS: readonly CustomerValueCrmTransparencyFieldDefinition[] = [
  {
    key: "criterionName",
    label: { de: "Kriterium", en: "Criterion" },
    purpose: { de: "Macht den Score-Treiber lesbar.", en: "Makes the score driver readable." },
    writebackObject: "company",
    writebackTiming: "future_version_only"
  },
  {
    key: "rawValue",
    label: { de: "Rohwert", en: "Raw value" },
    purpose: { de: "Zeigt den tatsächlich bewerteten Eingangswert.", en: "Shows the actual assessed input value." },
    writebackObject: "company",
    writebackTiming: "future_version_only"
  },
  {
    key: "points",
    label: { de: "Punkte", en: "Points" },
    purpose: { de: "Zeigt die 1-bis-10-Bewertung des Kriteriums.", en: "Shows the 1-to-10 criterion score." },
    writebackObject: "company",
    writebackTiming: "future_version_only"
  },
  {
    key: "weight",
    label: { de: "Gewichtung", en: "Weighting" },
    purpose: { de: "Zeigt den Anteil am Gesamtscore.", en: "Shows the share of the overall score." },
    writebackObject: "company",
    writebackTiming: "future_version_only"
  },
  {
    key: "weightedContribution",
    label: { de: "Gewichteter Beitrag", en: "Weighted contribution" },
    purpose: { de: "Macht den Score-Beitrag nachvollziehbar.", en: "Makes the score contribution traceable." },
    writebackObject: "company",
    writebackTiming: "future_version_only"
  },
  {
    key: "modelVersion",
    label: { de: "Modellversion", en: "Model version" },
    purpose: { de: "Dokumentiert die verwendete Bewertungslogik.", en: "Documents the scoring logic used." },
    writebackObject: "company",
    writebackTiming: "future_version_only"
  },
  {
    key: "evaluatedAt",
    label: { de: "Bewertet am", en: "Evaluated at" },
    purpose: { de: "Zeigt die Aktualität der Bewertung.", en: "Shows assessment freshness." },
    writebackObject: "company",
    writebackTiming: "future_version_only"
  },
  {
    key: "missingValue",
    label: { de: "Fehlender Wert", en: "Missing value" },
    purpose: { de: "Kennzeichnet unvollständige Eingangsdaten.", en: "Marks incomplete input data." },
    writebackObject: "company",
    writebackTiming: "future_version_only"
  },
  {
    key: "errorStatus",
    label: { de: "Fehlerstatus", en: "Error status" },
    purpose: { de: "Macht blockierte oder nicht bewertbare Kriterien transparent.", en: "Makes blocked or non-assessable criteria transparent." },
    writebackObject: "company",
    writebackTiming: "future_version_only"
  }
] as const;

export function criterionLabel<T extends string>(
  dictionary: LabelDictionary<T>,
  value: T,
  language: CustomerValueCriterionUiLanguage
): string {
  return dictionary[value]?.[language] ?? dictionary[value]?.en ?? value;
}

export function isTechnicalCriterionValueVisible(label: string): boolean {
  return /^(legacy|quantitative|qualitative|boolean|calculated|higher_is_better|lower_is_better|manual_bands|unconfigured|superseded)$/.test(label);
}

export function shouldShowQuantitativeFields(criterionType: CustomerValueCriterionType): boolean {
  return criterionType === "quantitative";
}

export function canSelectCriterionTypeForNewCriterion(criterionType: CustomerValueCriterionType): boolean {
  return criterionType !== "legacy";
}

export function decideCriterionDeletion(context: CustomerValueCriterionDeleteContext): CustomerValueCriterionDeleteDecision {
  if (context.criterion.scoringConfigurationStatus === "confirmed" || context.hasConfirmedScoringConfiguration) {
    return { allowed: false, code: "criterion_cannot_delete_confirmed" };
  }
  if (context.hasHistoricalUsage) return { allowed: false, code: "criterion_has_historical_usage" };
  if (context.hasActiveCrmMapping) return { allowed: false, code: "criterion_has_active_mapping" };
  return { allowed: true };
}

export function createTenPointBandSuggestion(
  criterionId: string,
  direction: CustomerValueScoringDirection
): CustomerValueCriterionScoringBand[] {
  const ascending = direction !== "lower_is_better";
  return Array.from({ length: 10 }, (_, index) => {
    const points = ascending ? index + 1 : 10 - index;
    return {
      id: `${criterionId}-suggested-band-${index + 1}`,
      criterionId,
      version: 1,
      points,
      minValue: index === 0 ? null : index * 10,
      maxValue: index === 9 ? null : (index + 1) * 10,
      minInclusive: true,
      maxInclusive: index === 9,
      rank: index + 1,
      status: "draft",
      active: true
    };
  });
}
